<template>
  <v-container
    class="pa-0"
    fluid
  >
    <v-row no-gutters>
      <v-col cols="12">
        <v-card flat>
          <v-card-title
            v-if="viewData && viewData.length"
            ref="filtersToolbar"
            class="pa-0"
          >
            <v-toolbar
              :height="$vuetify.breakpoint.xsOnly ? 44 : 60"
              class="filter-toolbar"
              flat
            >
              <v-btn
                :color="$vuetify.theme.dark ? '#1E1E1E' : 'rgba(0, 0, 0, 0.06)'"
                :style="$vuetify.breakpoint.xsOnly ? 'width: 36px; min-width: 36px; height: 36px;' : 'width: 48px; min-width: 48px; height: 48px;'"
                class="pa-0 ma-1 elevation-0"
                @click="toggleTableSize"
              >
                <v-icon
                  color="primary"
                  v-text="small ? 'unfold_more' : 'unfold_less'"
                />
              </v-btn>

              <print-button
                v-if="appHasFiscalDocsEnabled && appHasFiscalDocsProvider && appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD && !userIsWaiter"
                :color="$t('Restaurant.Tables.Toolbar.ReportButton.Color')"
                :icon-name="$t('Restaurant.Tables.Toolbar.ReportButton.Icon')"
                :label="$vuetify.breakpoint.xsOnly ? $t('Restaurant.Tables.Toolbar.ReportButton.TitleXs').toLocaleUpperCase($i18n.locale) : $t('Restaurant.Tables.Toolbar.ReportButton.Title').toLocaleUpperCase($i18n.locale)"
                btn-class="white--text ma-1"
                disable-tooltip
                flat
                large
                menu-offset-y
                @printer-selected="printer => Printer.printOpenTablesReport(printer)"
              />

              <v-spacer />

              <filter-search
                v-model="search"
              />

              <filter-options
                v-model="filtersModel"
                :filters="filterData"
              />
            </v-toolbar>
          </v-card-title>

          <v-card-text class="pa-0">
            <div
              ref="cardContainer"
              style="overflow-y: auto;"
            >
              <template v-if="viewData && viewData.length">
                <!-- Tables Accordion -->
                <v-expansion-panels
                  v-if="tableListExpandableEnabled"
                  v-model="panel"
                  :multiple="tableListExpandableMultiple"
                  accordion
                  class="transparent"
                  flat
                  tile
                >
                  <template v-for="(area, index) in viewDataFiltered">
                    <v-expansion-panel :key="`area-${index}-${area.Id}`">
                      <v-expansion-panel-header
                        class="spx-2 py-0"
                        style="max-height: 48px;min-height: 48px;height: 48px;"
                      >
                        <v-subheader class="pl-0">
                          <div
                            :style="`background-color: #${area.Color || '000000'};`"
                            class="d-inline-block"
                            style="width:5px; height: 22px; margin-right: 6px;"
                          />

                          <span class="grey--text text--darken-2 font-weight-bold text-subtitle-1 pr-1">
                            {{ area.Name && area.Name.toLocaleUpperCase($i18n.locale) }}
                          </span>&nbsp;
                          <span class="grey--text font-weight-regular mr-2">
                            {{ area.Tables.length }} {{ $tc('Restaurant.Tables.Title', area.Tables.length) }}
                          </span>

                          <template v-if="$vuetify.breakpoint.mdAndUp">
                            <table-area-tags
                              :icon-color="`#${area.Color || '000000'}`"
                              :tags="area.Tags"
                              label
                              x-small
                            />
                          </template>
                        </v-subheader>
                      </v-expansion-panel-header>

                      <v-expansion-panel-content>
                        <template v-if="$vuetify.breakpoint.smAndDown">
                          <table-area-tags
                            :icon-color="`#${area.Color || '000000'}`"
                            :tags="area.Tags"
                            class="mb-2"
                            label
                            x-small
                          />
                        </template>

                        <v-card
                          color="transparent"
                          flat
                        >
                          <v-card-text class="pa-0">
                            <v-container
                              class="pa-0"
                              fluid
                            >
                              <v-row dense>
                                <template v-for="table in area.Tables">
                                  <v-col
                                    :key="`area-${index}-${area.Id}-table-${table.Id}`"
                                    :cols="small ? 6 : 12"
                                    :lg="small ? 2 : 3"
                                    :md="small ? 2 : 4"
                                    :sm="small ? 3 : 6"
                                    :xl="small ? 1 : 2"
                                  >
                                    <v-lazy
                                      :height="small ? 38 : 175"
                                      :max-height="small ? 38 : 175"
                                      :min-height="small ? 38 : 175"
                                      :options="{threshold: 0.25}"
                                      transition="fade-transition"
                                    >
                                      <table-list-tile
                                        :area="area"
                                        :children-tables="getTableChildrenTables(table)"
                                        :disabled="table?.DisableActions"
                                        :small="small"
                                        :table="table"
                                        :toolbar="true"
                                        @click="onTableClick(table, area)"
                                        @click-persons="onPersonsClick(table, area)"
                                      />
                                    </v-lazy>
                                  </v-col>
                                </template>
                              </v-row>
                            </v-container>
                          </v-card-text>
                        </v-card>
                      </v-expansion-panel-content>

                      <v-divider />
                    </v-expansion-panel>
                  </template>
                </v-expansion-panels>

                <!-- Tables List -->
                <v-container
                  v-else
                  class="pa-2"
                  fluid
                >
                  <v-row dense>
                    <template v-for="(area, index) in viewDataFiltered">
                      <v-col
                        v-if="area.Tables && area.Tables.length > 0"
                        :key="`area-${area.Id}`"
                        :class="{'pt-4': index > 0}"
                        cols="12"
                      >
                        <v-subheader class="pl-0">
                          <div
                            :style="`background-color: #${area.Color || '000000'};`"
                            class="d-inline-block"
                            style="width:5px; height: 22px; margin-right: 6px;"
                          />

                          <span class="grey--text text--darken-2 font-weight-bold text-subtitle-1 pr-1">
                            {{ area.Name && area.Name.toLocaleUpperCase($i18n.locale) }}
                          </span>&nbsp;

                          <span class="grey--text font-weight-regular text-no-wrap mr-2">
                            {{ area.Tables.length }} {{ $tc('Restaurant.Tables.Title', area.Tables.length) }}
                          </span>

                          <template v-if="$vuetify.breakpoint.mdAndUp">
                            <table-area-tags
                              :icon-color="`#${area.Color || '000000'}`"
                              :tags="area.Tags"
                              label
                              x-small
                            />
                          </template>
                        </v-subheader>

                        <template v-if="$vuetify.breakpoint.smAndDown">
                          <table-area-tags
                            :icon-color="`#${area.Color || '000000'}`"
                            :tags="area.Tags"
                            label
                            x-small
                          />
                        </template>
                      </v-col>

                      <template v-for="table in area.Tables">
                        <v-col
                          :key="`area-${area.Id}-table-${table.Id}`"
                          :cols="small ? 6 : 12"
                          :lg="small ? 2 : 3"
                          :md="small ? 2 : 4"
                          :sm="small ? 3 : 6"
                          :xl="small ? 1 : 2"
                        >
                          <v-lazy
                            :height="small ? 38 : 175"
                            :max-height="small ? 38 : 175"
                            :min-height="small ? 38 : 175"
                            :options="{threshold: 0.25}"
                            transition="fade-transition"
                          >
                            <table-list-tile
                              :area="area"
                              :children-tables="getTableChildrenTables(table)"
                              :disabled="table?.DisableActions"
                              :small="small"
                              :table="table"
                              :toolbar="true"
                              @click="onTableClick(table, area)"
                              @click-persons="onPersonsClick(table, area)"
                            />
                          </v-lazy>
                        </v-col>
                      </template>
                    </template>
                  </v-row>
                </v-container>
              </template>

              <!-- No Waiter Active Shift -->
              <template v-if="userIsWaiter && viewData && viewData.error">
                <v-container
                  class="fill-height pa-2"
                  fluid
                >
                  <v-row
                    class="align-center fill-height ma-0 pa-0"
                  >
                    <v-col
                      class="text-center"
                      cols="12"
                    >
                      <div class="grey--text pa-1">
                        <v-icon
                          class="mb-2"
                          color="grey lighten-2"
                          size="128"
                        >
                          room_service
                        </v-icon>

                        <div class="text-h6">
                          {{ $t('Restaurant.Tables.NoData.Title') }}
                        </div>

                        <div
                          v-if="posUserHasComponentPermission('WaiterStaff', 'MANAGE')"
                          class="mt-4"
                        >
                          <v-btn
                            :to="{name: 'WaiterStaff'}"
                            class="elevation-0"
                            color="primary"
                          >
                            {{ $t('Restaurant.Tables.NoData.Button.ManageWaiters') }}
                          </v-btn>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-container>
              </template>

              <!-- Tables Loading Anim -->
              <template v-else-if="viewData && !viewData.length && loading">
                <v-container
                  class="fill-height pa-2"
                  fluid
                >
                  <v-row
                    class="align-center fill-height ma-0 pa-0"
                  >
                    <v-col
                      class="text-center"
                      cols="12"
                    >
                      <div
                        class="grey--text pa-1"
                      >
                        <v-icon
                          class="mb-2"
                          color="grey lighten-2"
                          size="128"
                        >
                          tab_unselected
                        </v-icon>

                        <div class="text-h6">
                          {{ $t('Common.Misc.WaitProgress.Title') }}
                        </div>

                        <div class="mt-4">
                          <v-progress-circular
                            :size="48"
                            :width="3"
                            color="green"
                            indeterminate
                          />
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-container>
              </template>

              <!-- No Tables Found -->
              <template v-else-if="viewData && !viewData.length">
                <v-container
                  class="fill-height pa-2"
                  fluid
                >
                  <v-row
                    class="align-center fill-height ma-0 pa-0"
                  >
                    <v-col
                      class="text-center"
                      cols="12"
                    >
                      <div class="grey--text pa-1">
                        <v-icon
                          class="mb-2"
                          color="grey lighten-2"
                          size="128"
                        >
                          tab_unselected
                        </v-icon>

                        <div class="text-h6">
                          {{ $t('Restaurant.Tables.NoTables.Title') }}
                        </div>

                        <div
                          v-if="posUserHasComponentPermission('TableManagement', 'MANAGE')"
                          class="mt-4"
                        >
                          <v-btn
                            :to="{name: 'TableManagement'}"
                            class="elevation-0"
                            color="primary"
                          >
                            {{ $t('Restaurant.Tables.NoTables.Button.ManageWaiters') }}
                          </v-btn>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-container>
              </template>
            </div>
          </v-card-text>
        </v-card>

        <template v-if="viewData && viewData.length">
          <table-options-menu-dialog
            :area="selectedArea"
            :table="selectedTable"
            :visible.sync="tableOptionsMenuDialogVisible"
          />

          <table-merge-split-dialog
            :area="selectedArea"
            :mode="tableMode"
            :table="selectedTable"
            :visible.sync="tableMergeSplitDialogVisible"
            @save-success="onTableMergeSplitSuccess"
          />

          <table-view-dialog
            :area="selectedArea"
            :table="selectedTable"
            :visible.sync="tableViewDialogVisible"
            @payment-success="onPaymentTableSuccess"
          />

          <table-edit-dialog
            :area="selectedArea"
            :table="selectedTable"
            :visible.sync="tableEditDialogVisible"
            @save-success="(data, payload) => getTableUpdate(payload?.TableId)"
          />

          <table-order-items-move-dialog
            :area="selectedArea"
            :table="selectedTable"
            :visible.sync="tableOrderItemsMoveDialogVisible"
            @save-success="onOrderItemsMoveSuccess"
          />

          <table-change-waiter-dialog
            :area="selectedArea"
            :table="selectedTable"
            :visible.sync="tableChangeWaiterDialogVisible"
            @save-success="onTableChangeWaiterSuccess"
          />

          <table-payment-select-menu-item-dialog
            :area="selectedArea"
            :table="selectedTable"
            :visible.sync="tablePaymentSelectMenuItemDialogVisible"
            @payment-success="onPaymentTableSuccess"
          />

          <select-table-dialog
            :exclude="[selectedTable]"
            :tables="viewData"
            :visible.sync="selectTableDialogVisible"
          />

          <select-waiter-staff-dialog
            :visible.sync="selectWaiterDialogVisible"
            @on:waiter-staff-selected="onSelectWaiter"
            @waiter-staff-selected="onSelectWaiter"
          />

          <set-table-persons-dialog
            :area="selectedArea"
            :merged-max-capacity="getTableTotalMaxCapacity(selectedTable)"
            :merged-min-capacity="getTableTotalMinCapacity(selectedTable)"
            :table="selectedTable"
            :visible.sync="setTablePersonsDialogVisible"
            @on:persons-set="onSetTablePersons"
          />

          <confirm-dialog
            :cancel-button-text="confirmCloseTableSwiftDialog.Button.Cancel"
            :html-content="confirmCloseTableSwiftDialog.Body"
            :html-title="confirmCloseTableSwiftDialog.Title"
            :info-bar="true"
            :info-bar-body="confirmCloseTableSwiftDialog.Info"
            :no-button-class="confirmCloseTableSwiftDialog.Button?.NoClass ? confirmCloseTableSwiftDialog.Button.NoClass : 'green white--text'"
            :no-button-text="confirmCloseTableSwiftDialog.Button.No"
            :title-class="confirmCloseTableSwiftDialog?.TitleClass ? confirmCloseTableSwiftDialog.TitleClass : 'red white--text'"
            :visible.sync="confirmCloseTableSwiftDialogVisible"
            :width="620"
            :yes-button-class="confirmCloseTableSwiftDialog.Button?.YesClass ? confirmCloseTableSwiftDialog.Button.YesClass : 'red white--text'"
            :yes-button-text="confirmCloseTableSwiftDialog.Button.Yes"
            cancel-button
            @confirm-dialog-no="onConfirmNoCloseAndDiscardSwift"
            @confirm-dialog-yes="onConfirmYesCloseAndDiscardSwift"
            @confirm-dialog-cancel="onConfirmCancel"
          />
        </template>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import AppData from '@/mixins/appdata'
import Auth from '@/mixins/auth'
import TableOptionsMenuDialog from '@/components/restaurant/tables/tables/dialogs/menu/TableOptionsMenuDialog'
import TableListTile from '@/components/restaurant/tables/tables/TableListTile'
import TableMergeSplitDialog from '@/components/restaurant/tables/tables/dialogs/TableMergeSplitDialog'
import TableStatus from '@/mixins/tables/tableStatus'
import TableCommon from '@/mixins/tables/tableCommon'
import Resizable from '@/mixins/resizable'
import TableMergeSplitModeEnum from '@/api/Enums/TableMergeSplitModeEnum'
import TableViewDialog from '@/components/restaurant/tables/tables/dialogs/TableViewDialog'
import TableOrderItemsMoveDialog from '@/components/restaurant/tables/tables/dialogs/TableOrderItemsMoveDialog'
import SelectTableDialog from '@/components/restaurant/tables/tables/dialogs/SelectTableDialog'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import greekUtils from '@/lib/greek-utils'
import TablePaymentSelectMenuItemDialog from '@/components/restaurant/tables/payments/dialogs/TablePaymentSelectMenuItemDialog'
import TableEditDialog from '@/components/restaurant/tables/tables/dialogs/TableEditDialog'
import SetTablePersonsDialog from '@/components/restaurant/tables/tables/dialogs/SetTablePersonsDialog.vue'
import TableChangeWaiterDialog from '@/components/restaurant/tables/tables/dialogs/TableChangeWaiterDialog.vue'
import FilterSearch from '@/components/common/filter/FilterSearch.vue'
import FilterOptions from '@/components/common/filter/FilterOptions.vue'
import FilterCommon from '@/mixins/filter/filterCommon'
import SelectWaiterStaffDialog from '@/components/waiters/SelectWaiterStaffDialog.vue'
import TableActions from '@/mixins/tables/tableActions'
import TableStatusEnum from '@/api/Enums/TableStatusEnum'
import TableAreaTags from '@/components/restaurant/tables/common/TableAreaTags.vue'
import {clone} from '@/lib/helper/helper'
import PinManagerEnum from '@/api/Enums/PinManagerEnum'
import PrintButton from '@/components/common/print/PrintButton.vue'
import Printer from '@/lib/print/printer.js'
import FiscalDocsProviderTypeEnum from '@/api/Enums/FiscalDocsProviderTypeEnum'
import * as SocketTableHelper from '@/api/SocketTableHelper.js'

export default {
  name      : 'Tables',
  components: {
    PrintButton,
    TableAreaTags,
    SelectWaiterStaffDialog,
    FilterOptions,
    FilterSearch,
    TableChangeWaiterDialog,
    SetTablePersonsDialog,
    TableEditDialog,
    TablePaymentSelectMenuItemDialog,
    ConfirmDialog,
    SelectTableDialog,
    TableOrderItemsMoveDialog,
    TableViewDialog,
    TableMergeSplitDialog,
    TableListTile,
    TableOptionsMenuDialog
  },
  directives: {},
  mixins    : [AppData, Auth, TableStatus, TableCommon, TableActions, Resizable, FilterCommon],
  props     : {},
  DataStore : {tables: 'tables'},
  data() {
    return {
      filtersModel: {
        lang           : 'Restaurant.Tables',
        filterTableArea: {
          value: [],
          items: []
        },
        filterTableStatus: {
          value: [],
          items: []
        },
        filterWaiterStaff: {
          value: null,
          items: []
        }
      },
      search                                 : '',
      panel                                  : 0,
      viewData                               : null,
      small                                  : false,
      loading                                : false,
      selectedWaiter                         : null,
      selectedTable                          : null,
      selectedArea                           : null,
      tableAreaCombo                         : [],
      tableOptionsMenuDialogVisible          : false,
      tablePaymentSelectMenuItemDialogVisible: false,
      tableMergeSplitDialogVisible           : false,
      tableViewDialogVisible                 : false,
      tableEditDialogVisible                 : false,
      tableOrderItemsMoveDialogVisible       : false,
      tableChangeWaiterDialogVisible         : false,
      setTablePersonsDialogVisible           : false,
      selectTableDialogVisible               : false,
      selectWaiterDialogVisible              : false,
      confirmCloseTableSwiftDialogVisible    : false,
      confirmCloseTableSwiftDialog           : {
        Title : '',
        Body  : '',
        Info  : '',
        Button: {
          Yes   : '',
          No    : '',
          Cancel: ''
        }
      },
      tableMode: TableMergeSplitModeEnum.Merge
    }
  },
  computed: {
    FiscalDocsProviderTypeEnum() {
      return FiscalDocsProviderTypeEnum
    },
    Printer() {
      return Printer
    },
    tableListExpandable() {
      return this.appConfig?.LOCATION_DATA?.TableListExpandable || {
        Enabled        : true,
        Desktop        : false,
        MultipleDesktop: false,
        Mobile         : true,
        MultipleMobile : false
      }
    },

    tableListExpandableEnabled() {
      if (!this.tableListExpandable.Enabled) return false

      if (this.tableListExpandable.Mobile && this.tableListExpandable.Desktop) return true
      if (this.$vuetify.breakpoint.xsOnly && this.tableListExpandable.Mobile) return true
      if (this.$vuetify.breakpoint.smAndUp && this.tableListExpandable.Desktop) return true

      return false
    },

    tableListExpandableMultiple() {
      if (this.tableListExpandable.Mobile && this.tableListExpandable.MultipleMobile &&
          this.tableListExpandable.Desktop && this.tableListExpandable.MultipleDesktop) {
        return true
      }

      if (this.$vuetify.breakpoint.xsOnly && this.tableListExpandable.Mobile && this.tableListExpandable.MultipleMobile) return true
      if (this.$vuetify.breakpoint.smAndUp && this.tableListExpandable.Desktop && this.tableListExpandable.MultipleDesktop) return true

      return false
    },

    tableAreaComboFiltered() {
      const data = Array.isArray(this.tableAreaCombo) ? this.tableAreaCombo : []
      return data?.filter(area => area.Active)?.map(o => {
        o.Avatar = {Color: `#${o.Color}`}
        return o
      })
    },

    tableStatusComboFiltered() {
      return Object.values(this.$t('Restaurant.Tables.TableStatus')).filter(status => status.Active).map(o => {
        o.Avatar = {Color: o.Color}
        return o
      })
    },

    waiterStaffFiltered() {
      const staff = window.Vue.waiterStaffItems?.filter(waiter => waiter.Status && waiter.HasActiveShift) || []
      return staff.map(o => {
        o.Name = `${o.Firstname} ${o.Lastname}`
        o.Avatar = {
          Color: o.Color ? `#${o.Color}` : 'grey darken-1',
          Icon : 'account_circle'
        }
        return o
      })
    },

    viewDataFiltered() {
      const filterArea = this.filtersModel.filterTableArea.value || []
      const filterStatus = this.filtersModel.filterTableStatus.value || []
      const filterWaiter = this.filtersModel.filterWaiterStaff.value || ''
      const filterSearch = this.search ? greekUtils.toGreeklish(this.search.toLocaleLowerCase(this.$i18n.locale).trim()) : ''

      let data = JSON.parse(JSON.stringify(this.viewData || []))
      //let data = this.viewData || []
      data = data.filter(area => filterArea.length ? filterArea.includes(area.Id) : true)

      data.forEach(area => {
        area.Tables = area.Tables
          .filter(table => !table.Parent)
          .filter(table => filterStatus.length ? filterStatus.includes(parseInt(table.StatusId)) : true)
          .filter(table => filterSearch ? greekUtils.toGreeklish(table.Name.toLocaleLowerCase(this.$i18n.locale).trim()).includes(filterSearch) : true)
          .filter(table => filterWaiter ? !table?.StaffId || table?.StaffId === parseInt(filterWaiter) : true)
      })

      return data
    }
  },
  watch: {},
  beforeCreate() {
  },
  created() {
    this.small = this.$localStorage.get('tbl', false)
    this.viewData = this.tables

    // Init Custom Filter Values
    this.filtersModel.filterTableArea.items = this.tableAreaComboFiltered
    this.filtersModel.filterTableStatus.items = this.tableStatusComboFiltered
    this.filtersModel.filterWaiterStaff.items = this.waiterStaffFiltered
  },
  beforeMount() {
  },
  mounted() {
    this.$bus.$on(window.SocketCommand.Table.AllByArea, this.setData)
    this.$bus.$on(window.SocketCommand.Table.Area.List, this.setTableAreaCombo)
    this.$bus.$on(window.SocketCommand.Table.Close, this.onCloseTableResponse)
    this.$bus.$on(window.SocketCommand.Table.Open, this.onOpenTableResponse)
    this.$bus.$on(window.SocketCommand.Staff.Waiter.Shift.Changed, this.onWaiterStaffShiftChanged)
    this.$bus.$on(window.SocketCommand.Table.SetAwaitingPaymentStatus, this.onPaymentTableSuccess)
    this.$bus.$on(window.SocketCommand.Table.UnsetAwaitingPaymentStatus, this.onPaymentTableSuccess)
    this.$bus.$on(window.SocketCommand.Table.ChangePersons, this.onSetTablePersonsResponse)
    this.$bus.$on('hide-app-offline-modal', this.onConnectionRestore)
    this.$bus.$on('click:table-new-order-button', this.onNewOrderButtonClick)
    this.$bus.$on('click:table-merge-button', this.onTableMergeButtonClick)
    this.$bus.$on('click:table-split-button', this.onTableSplitButtonClick)
    this.$bus.$on('click:table-view-button', this.onTableViewButtonClick)
    this.$bus.$on('click:table-edit-button', this.onTableEditButtonClick)
    this.$bus.$on('click:table-order-items-move-button', this.onTableMoveButtonClick)
    this.$bus.$on('click:table-change-waiter-button', this.onTableChangeWaiterButtonClick)
    this.$bus.$on('click:table-select-button', this.onSelectTableButtonClick)
    this.$bus.$on('click:table-close-button', this.onCloseTableButtonClick)
    this.$bus.$on('click:table-open-button', this.onOpenTableButtonClick)
    this.$bus.$on('click:table-reservation-button', this.onReserveTableButtonClick)
    this.$bus.$on('click:table-pay-button', this.onPayTableButtonClick)
    this.$bus.$on('click:payment-pay-table-menu-item-button', this.onPaymentTableMenuItemButtonClick)
    this.$bus.$on('click:set-awaiting-payment-table-status-button', this.onSetAwaitingPaymentStatusButtonClick)
    this.$bus.$on('click:unset-awaiting-payment-table-status-button', this.onUnsetAwaitingPaymentStatusButtonClick)

    this.getTableAreaCombo()
    this.initWaiterFilters()

    setTimeout(() => {
      this.getData()
    }, 250)
  },
  beforeUpdate() {
  },
  updated() {
  },
  beforeDestroy() {
    this.$bus.$off(window.SocketCommand.Table.AllByArea, this.setData)
    this.$bus.$off(window.SocketCommand.Table.Area.List, this.setTableAreaCombo)
    this.$bus.$off(window.SocketCommand.Table.Close, this.onCloseTableResponse)
    this.$bus.$off(window.SocketCommand.Table.Open, this.onOpenTableResponse)
    this.$bus.$off(window.SocketCommand.Staff.Waiter.Shift.Changed, this.onWaiterStaffShiftChanged)
    this.$bus.$off(window.SocketCommand.Table.SetAwaitingPaymentStatus, this.onPaymentTableSuccess)
    this.$bus.$off(window.SocketCommand.Table.UnsetAwaitingPaymentStatus, this.onPaymentTableSuccess)
    this.$bus.$off(window.SocketCommand.Table.ChangePersons, this.onSetTablePersonsResponse)
    this.$bus.$off('hide-app-offline-modal', this.onConnectionRestore)
    this.$bus.$off('click:table-new-order-button', this.onNewOrderButtonClick)
    this.$bus.$off('click:table-merge-button', this.onTableMergeButtonClick)
    this.$bus.$off('click:table-split-button', this.onTableSplitButtonClick)
    this.$bus.$off('click:table-view-button', this.onTableViewButtonClick)
    this.$bus.$off('click:table-edit-button', this.onTableEditButtonClick)
    this.$bus.$off('click:table-order-items-move-button', this.onTableMoveButtonClick)
    this.$bus.$off('click:table-change-waiter-button', this.onTableChangeWaiterButtonClick)
    this.$bus.$off('click:table-select-button', this.onSelectTableButtonClick)
    this.$bus.$off('click:table-close-button', this.onCloseTableButtonClick)
    this.$bus.$off('click:table-open-button', this.onOpenTableButtonClick)
    this.$bus.$off('click:table-reservation-button', this.onReserveTableButtonClick)
    this.$bus.$off('click:table-pay-button', this.onPayTableButtonClick)
    this.$bus.$off('click:payment-pay-table-menu-item-button', this.onPaymentTableMenuItemButtonClick)
    this.$bus.$off('click:set-awaiting-payment-table-status-button', this.onSetAwaitingPaymentStatusButtonClick)
    this.$bus.$off('click:unset-awaiting-payment-table-status-button', this.onUnsetAwaitingPaymentStatusButtonClick)
  },
  destroyed() {
  },
  methods: {
    onConnectionRestore() {
      this.getData()
    },

    getTableRefence(table, data) {
      if (!table) return null
      const areas = Array.isArray(data) ? data : []
      const areaIdx = areas.findIndex(area => area.Id === table?.AreaId)

      if (areaIdx > -1) {
        const tableIdx = areas[areaIdx].Tables?.findIndex(tbl => tbl.Id === table.Id)
        if (tableIdx > -1) return areas[areaIdx].Tables[tableIdx] || null
      }

      return null
    },

    disableTableActions(table) {
      if (!table) return

      const tblRef1 = this.getTableRefence(table, this.viewData)
      const tblRef2 = this.getTableRefence(table, this.viewDataFiltered)
      if (tblRef1) this.$set(tblRef1, 'DisableActions', true)
      if (tblRef2) this.$set(tblRef2, 'DisableActions', true)
    },

    enableTableActions(table) {
      if (!table) return

      const tblRef1 = this.getTableRefence(table, this.viewData)
      const tblRef2 = this.getTableRefence(table, this.viewDataFiltered)
      if (tblRef1) this.$set(tblRef1, 'DisableActions', false)
      if (tblRef2) this.$set(tblRef2, 'DisableActions', false)
    },

    tablesAreaAvailableTables(area) {
      return area?.Tables?.filter(table => this.tableIsAvailable(table)) || []
    },

    toggleTableSize() {
      this.small = !this.small
      this.$localStorage.set('tbl', this.small)
    },

    initWaiterFilters() {
      if (!this.tables.length) return

      const waiter = this.waiterStaffFiltered?.find(waiter => parseInt(waiter.Id) === parseInt(this.posUser.staff_id))
      const currentUserHasActiveWaiterShift = !!waiter
      if (currentUserHasActiveWaiterShift && !this.appConfig?.LOCATION_DATA?.WaiterCanManageAllTables) {
        this.filtersModel.filterWaiterStaff.value = String(this.posUser.staff_id)
      }

      if (currentUserHasActiveWaiterShift) {
        const waiterTableAreas = waiter?.TableAreas || []
        if (waiterTableAreas.length > 0 && waiterTableAreas[0] > 0) {
          this.filtersModel.filterTableArea.value = waiterTableAreas
        }
      }
    },

    getTableUpdate(tableId) {
      if (!tableId) return

      SocketTableHelper.getTable(tableId)
    },

    getData() {
      this.loading = true
      window.callAS(window.SocketCommand.Table.AllByArea, {})
    },
    setData(data) {
      this.$set(this, 'tables', data || [])
      this.$set(this, 'viewData', this.tables || [])

      this.loading = false

      if (!this.tables.length) return

      setTimeout(() => {
        this.initWaiterFilters()
      }, 100)

      this.updateSelectedTable()
    },

    updateSelectedTable() {
      const tableAreas = Array.isArray(this.viewData) ? this.viewData : []

      if (this.selectedArea) {
        const area = tableAreas.find(area => area.Id === this.selectedArea)
        if (area) this.selectedArea = clone(area)
      }

      const tblRef1 = this.getTableRefence(this.selectedTable, this.viewData)
      const tblRef2 = this.getTableRefence(this.selectedTable, this.viewDataFiltered)
      if (tblRef1) {
        const disableActions = tblRef1.StatusId !== TableStatusEnum.Available ? tblRef1?.DisableActions ?? false : false
        this.$set(tblRef1, 'DisableActions', disableActions)
        this.selectedTable = tblRef1
      }
      if (tblRef2) {
        const disableActions = tblRef2.StatusId !== TableStatusEnum.Available ? tblRef2?.DisableActions ?? false : false
        this.$set(tblRef2, 'DisableActions', disableActions)
        this.selectedTable = tblRef2
      }
    },

    getTableAreaCombo() {
      window.callAS(window.SocketCommand.Table.Area.List)
    },
    setTableAreaCombo(data) {
      this.tableAreaCombo = Array.isArray(data) ? data : []
      this.filtersModel.filterTableArea.items = this.tableAreaComboFiltered
    },

    onTableClick(table, area) {
      if (!table || !area) return
      this.selectedTable = table
      this.selectedArea = area
      this.tableOptionsMenuDialogVisible = true
    },

    onPersonsClick(table, area) {
      if (!table || !area) return
      this.selectedTable = clone(table)
      this.selectedArea = area
      this.setTablePersonsDialogVisible = true
    },

    onNewOrderButtonClick(table, area) {
      if (!table || !area) return
      this.$router.push({
        name  : 'Pos',
        params: {
          tableId: table.Id,
          table  : table,
          area   : area
        }
      })
    },

    onTableMergeButtonClick(table, area) {
      if (!table || !area) return
      const data = JSON.parse(JSON.stringify(this.viewData))
      const selectedArea = data.find(originalArea => originalArea.Id === area.Id)
      this.selectedTable = clone(table)
      this.selectedArea = selectedArea
      this.tableMode = TableMergeSplitModeEnum.Merge
      this.tableMergeSplitDialogVisible = true
    },
    onTableSplitButtonClick(table, area) {
      if (!table || !area) return
      const data = JSON.parse(JSON.stringify(this.viewData))
      const selectedArea = data.find(originalArea => originalArea.Id === area.Id)
      this.selectedTable = clone(table)
      this.selectedArea = selectedArea
      this.tableMode = TableMergeSplitModeEnum.Split
      this.tableMergeSplitDialogVisible = true
    },
    onTableMergeSplitSuccess(data, tblIds, result) {
      this.tableOptionsMenuDialogVisible = false
      this.tableMergeSplitDialogVisible = false

      if (Array.isArray(result?.ids) && result?.ids?.length > 0) {
        SocketTableHelper.getTable(result?.ids)
      } else {
        this.getData()
      }
    },

    onTableViewButtonClick(table, area) {
      if (!table || !area) return
      const data = JSON.parse(JSON.stringify(this.viewData))
      const selectedArea = data.find(originalArea => originalArea.Id === area.Id)
      this.selectedTable = clone(table)
      this.selectedArea = selectedArea
      this.tableViewDialogVisible = true
    },

    onTableEditButtonClick(table, area) {
      if (!table || !area) return
      const data = JSON.parse(JSON.stringify(this.viewData))
      const selectedArea = data.find(originalArea => originalArea.Id === area.Id)
      this.selectedTable = clone(table)
      this.selectedArea = selectedArea
      this.tableEditDialogVisible = true
    },

    onTableMoveButtonClick(table, area) {
      if (!table || !area) return
      const data = JSON.parse(JSON.stringify(this.viewData))
      const selectedArea = data.find(originalArea => originalArea.Id === area.Id)
      this.selectedTable = clone(table)
      this.selectedTable.ChildrenTables = this.getTableChildrenTables(table)
      this.selectedArea = selectedArea
      this.tableOrderItemsMoveDialogVisible = true
    },
    onOrderItemsMoveSuccess(data, payload) {
      this.tableOptionsMenuDialogVisible = false
      this.tableOrderItemsMoveDialogVisible = false

      if (payload?.SourceTableId && payload?.TargetTableId) {
        SocketTableHelper.getTable([payload.SourceTableId, payload.TargetTableId])
      } else {
        this.getData()
      }
    },

    onTableChangeWaiterButtonClick(table, area) {
      if (!table || !area) return
      const data = JSON.parse(JSON.stringify(this.viewData))
      const selectedArea = data.find(originalArea => originalArea.Id === area.Id)
      this.selectedTable = clone(table)
      this.selectedTable.ChildrenTables = this.getTableChildrenTables(table)
      this.selectedArea = selectedArea

      this.executeWithPinPermissionManager(this.userNeedsPinPermission(PinManagerEnum.ChangeTableWaiter) && this.tableIsOwn(table), (success) => {
        if (!success) return

        this.tableChangeWaiterDialogVisible = true
      }, 'change-table-waiter')
    },
    onTableChangeWaiterSuccess(data, payload) {
      this.tableOptionsMenuDialogVisible = false
      this.tableChangeWaiterDialogVisible = false

      if (payload?.TableId) {
        SocketTableHelper.getTable(payload?.TableId)
      } else {
        this.getData()
      }
    },

    onSelectTableButtonClick(table) {
      if (!table) return
      this.selectedTable = clone(table)
      this.selectedTable.ChildrenTables = this.getTableChildrenTables(table)
      this.selectTableDialogVisible = true
    },

    onCloseTableButtonClick(table, area) {
      if (!table || !area) return
      this.selectedTable = table
      this.selectedArea = clone(area)

      if (this.tableHasUnPaidItems(table)) {
        if (this.tableHasCartsOpened(table)) {
          this.confirmCloseTableSwiftDialog = {
            Title : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftAndDiscardOrderWithOpenCarts.Title'),
            Body  : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftAndDiscardOrderWithOpenCarts.Content', {table: `${area.Name} ${table.Name}`}),
            Info  : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftAndDiscardOrderWithOpenCarts.Notification'),
            Button: {
              Yes   : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftAndDiscardOrderWithOpenCarts.Button.Yes'),
              No    : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftAndDiscardOrderWithOpenCarts.Button.No'),
              Cancel: this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftAndDiscardOrderWithOpenCarts.Button.Cancel')
            }
          }
        } else {
          if (this.tableHasPaidItems(table)) {
            this.confirmCloseTableSwiftDialog = {
              Title : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftAndDiscardOrderPartialPaid.Title'),
              Body  : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftAndDiscardOrderPartialPaid.Content', {table: `${area.Name} ${table.Name}`}),
              Info  : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftAndDiscardOrderPartialPaid.Notification'),
              Button: {
                Yes   : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftAndDiscardOrderPartialPaid.Button.Yes'),
                No    : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftAndDiscardOrderPartialPaid.Button.No'),
                Cancel: this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftAndDiscardOrderPartialPaid.Button.Cancel')
              }
            }
          } else {
            this.confirmCloseTableSwiftDialog = {
              Title : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftAndDiscardOrder.Title'),
              Body  : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftAndDiscardOrder.Content', {table: `${area.Name} ${table.Name}`}),
              Info  : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftAndDiscardOrder.Notification'),
              Button: {
                Yes   : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftAndDiscardOrder.Button.Yes'),
                No    : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftAndDiscardOrder.Button.No'),
                Cancel: this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftAndDiscardOrder.Button.Cancel')
              }
            }
          }
        }

        this.confirmCloseTableSwiftDialogVisible = true

      } else {
        if (this.tableHasCartsOpened(table)) {
          this.confirmCloseTableSwiftDialog = {
            Title : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftWithOpenCarts.Title'),
            Body  : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftWithOpenCarts.Content', {table: `${area.Name} ${table.Name}`}),
            Info  : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftWithOpenCarts.Notification'),
            Button: {
              Yes   : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftWithOpenCarts.Button.Yes'),
              No    : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftWithOpenCarts.Button.No'),
              Cancel: this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftWithOpenCarts.Button.Cancel')
            }
          }

          this.confirmCloseTableSwiftDialogVisible = true

        } else {
          this.confirmCloseTableSwiftDialog = {
            Title     : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftAndDiscardOrderPaid.Title'),
            TitleClass: 'green white--text',
            Body      : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftAndDiscardOrderPaid.Content', {table: `${area.Name} ${table.Name}`}),
            Info      : '',
            Button    : {
              Yes     : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftAndDiscardOrderPaid.Button.Yes'),
              YesClass: 'green white--text',
              No      : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftAndDiscardOrderPaid.Button.No'),
              NoClass : 'grey white--text',
              Cancel  : this.$t('Restaurant.Tables.Dialog.ConfirmCloseSwiftAndDiscardOrderPaid.Button.Cancel')
            }
          }

          this.confirmCloseTableSwiftDialogVisible = true
        }
      }
    },

    onConfirmYesCloseAndDiscardSwift() {
      const selectedTable1 = clone(this.getTableRefence(this.selectedTable, this.viewData))
      const selectedTable2 = clone(this.getTableRefence(this.selectedTable, this.viewDataFiltered))
      const payload = {
        TableId: selectedTable1.Id,
        CartId : selectedTable1?.CartId || null
      }

      if (selectedTable1.DisableActions || selectedTable2.DisableActions) return

      this.executeWithPinPermissionManager(this.tableHasUnPaidItems(this.selectedTable) && PinManagerEnum.DocumentCancel, (success) => {
        if (!success) return

        this.disableTableActions(selectedTable1, payload)
        this.disableTableActions(selectedTable2, payload)

        window.callAS(window.SocketCommand.Table.Close, payload)
        this.selectedTable = null
      })
    },

    onConfirmCancel() {
      this.enableTableActions(this.selectedTable)
      this.selectedTable = null
    },

    onConfirmNoCloseAndDiscardSwift() {
      if (this.tableHasUnPaidItems(this.selectedTable)) {
        this.enableTableActions(this.selectedTable)
        this.$emitAction('click:table-pay-button')
      } else {
        this.confirmCloseTableSwiftDialogVisible = false
      }
    },

    onCloseTableResponse(data, payload) {
      if (data.status === 'success') {
        this.tableOptionsMenuDialogVisible = false
        if (payload?.TableId) {
          SocketTableHelper.getTable(payload?.TableId)
        } else {
          this.getData()
        }
      } else {
        this.enableTableActions(this.selectedTable)
        this.$bus.$emit('app-show-notification', {
          body   : this.$t('Common.Error.Generic'),
          type   : 'error',
          icon   : 'warning',
          timeout: 3000
        })
        SocketTableHelper.getTable(payload?.TableId)
      }
    },

    onSelectWaiter(waiter) {
      if (!waiter) return

      this.selectedWaiter = waiter

      const openTableWithDefaultPersons = this.appConfig?.LOCATION_DATA?.OpenTableWithDefaultPersons || false
      if (openTableWithDefaultPersons) {
        this.openTable(this.selectedTable, this.selectedArea, null, waiter)
      } else {
        this.setTablePersonsDialogVisible = true
      }
    },

    onReserveTableButtonClick(table, area) {
      if (!table) return

      this.selectedTable = clone(table)
      this.selectedArea = clone(area)

      this.$bus.$emit('show-table-reservation-dialog', {
        Table: this.selectedTable,
        Area : this.selectedArea
      })
    },

    onOpenTableButtonClick(table, area) {
      if (!table) return

      const openTableWithDefaultPersons = this.appConfig?.LOCATION_DATA?.OpenTableWithDefaultPersons || false
      this.selectedTable = clone(table)
      this.selectedArea = clone(area)
      this.tableOptionsMenuDialogVisible = false

      if (this.userIsWaiter) {
        if (openTableWithDefaultPersons) {
          this.openTable(this.selectedTable, this.selectedArea)
        } else {
          this.setTablePersonsDialogVisible = true
        }
      } else {
        this.selectWaiterDialogVisible = true
      }
    },

    onSetTablePersons(table, area, persons) {
      if (!table) return

      if (table.StatusId === TableStatusEnum.Available) {
        this.onOpenTableWithPersons(table, area, persons)
      } else {
        const payload = {
          TableId: table.Id,
          ShiftId: table.ShiftId,
          Persons: persons
        }
        window.callAS(window.SocketCommand.Table.ChangePersons, payload)
      }
    },
    onSetTablePersonsResponse(data, payload) {
      if (data.status === 'success') {
        if (payload?.TableId) {
          SocketTableHelper.getTable(payload?.TableId)
        } else {
          this.getData()
        }
      } else {
        this.$bus.$emit('app-show-notification', {
          body   : this.$t('Common.Error.Generic'),
          type   : 'error',
          icon   : 'warning',
          timeout: 3000
        })
        SocketTableHelper.getTable(payload?.TableId)
      }
    },

    onOpenTableWithPersons(table, area, persons) {
      if (!table) return

      if (this.selectedWaiter) {
        this.openTable(this.selectedTable, this.selectedArea, persons, this.selectedWaiter)
      } else {
        this.openTable(table, area, persons)
      }
    },

    openTable(table, area, persons, waiter) {
      if (!table) return

      this.disableTableActions(table)
      const openTableWithDefaultPersons = this.appConfig?.LOCATION_DATA?.OpenTableWithDefaultPersons || false
      const minCapacity = openTableWithDefaultPersons ? table.MinCapacity || 1 : 1
      const maxCapacity = openTableWithDefaultPersons ? table.MaxCapacity || 1 : 10000

      persons = parseInt(persons) || 0

      if (persons < minCapacity) persons = minCapacity
      if (persons > maxCapacity) persons = maxCapacity

      const data = {
        TableId: table.Id,
        AreaId : table.AreaId,
        Persons: persons,
        StaffId: waiter?.Id || null
      }
      window.callAS(window.SocketCommand.Table.Open, data)

      this.selectedWaiter = null
    },
    onOpenTableResponse(data, payload) {
      this.tableOptionsMenuDialogVisible = false
      if (payload?.TableId) {
        SocketTableHelper.getTable(payload?.TableId)
      } else {
        this.getData()
      }

      if (data.status !== 'success') {
        this.$bus.$emit('app-show-notification', {
          body   : this.$t('Common.Error.Generic'),
          type   : 'error',
          icon   : 'warning',
          timeout: 3000
        })
      }
    },

    onWaiterStaffShiftChanged(data) {
      if (parseInt(this.posUser.staff_id) === parseInt(data.id)) {
        this.getData()

        if (data.open && !this.appConfig?.LOCATION_DATA?.WaiterCanManageAllTables) {
          this.filtersModel.filterWaiterStaff.value = String(this.posUser.staff_id)
        } else {
          this.filtersModel.filterWaiterStaff.value = null
        }
      }
    },

    onPayTableButtonClick(table, area) {
      if (!table || !area) return
      this.tableOptionsMenuDialogVisible = false
      this.onPaymentTableMenuItemButtonClick(table, area)
    },

    onPaymentTableMenuItemButtonClick(table, area) {
      if (!table || !area) return
      this.selectedTable = clone(table)
      this.selectedArea = area
      this.tablePaymentSelectMenuItemDialogVisible = true
    },

    onSetAwaitingPaymentStatusButtonClick(table, area) {
      if (!table || !area) return
      this.selectedTable = clone(table)
      this.selectedArea = area
      this.tableOptionsMenuDialogVisible = false

      window.callAS(window.SocketCommand.Table.SetAwaitingPaymentStatus, {TableId: this.selectedTable.Id})
    },

    onUnsetAwaitingPaymentStatusButtonClick(table, area) {
      if (!table || !area) return
      this.selectedTable = clone(table)
      this.selectedArea = area
      this.tableOptionsMenuDialogVisible = false

      window.callAS(window.SocketCommand.Table.UnsetAwaitingPaymentStatus, {
        CartId : this.selectedTable.CartId,
        TableId: this.selectedTable.Id
      })
    },

    onPaymentTableSuccess(data, payload) {
      this.tablePaymentSelectMenuItemDialogVisible = false

      if (payload?.TableId) {
        SocketTableHelper.getTable(payload?.TableId)
      } else {
        this.getData()
      }
    },

    onResize() {
      const container = this.$refs.cardContainer || null
      const filtersToolbar = this.$refs.filtersToolbar || null

      const headerHeight = document.getElementById('appToolbar')?.offsetHeight ? document.getElementById('appToolbar').offsetHeight : 0
      const footerHeight = document.getElementById('appFooter')?.offsetHeight ? document.getElementById('appFooter').offsetHeight : 0
      const toolbarHeight = filtersToolbar?.offsetHeight || 48

      if (container) container.style.height = (innerHeight - headerHeight - footerHeight - toolbarHeight) + 'px'
    }
  }
}
</script>

<style scoped>
:deep(.filter-toolbar .v-toolbar__content) {
  padding: 0 6px;
}

:deep(.v-expansion-panel-content__wrap) {
  padding: 4px 8px;
}

:deep(.v-expansion-panel__header) {
  padding: 8px 8px;
  min-height: 48px;
  height: 48px;
}
</style>
